import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { observer } from "@transact/observe";
import { ErrorBoundary, bootTransactTheme, installRefusalFocus } from "@transact/ui";
import App from "./App.js";
import { hostErrors } from "./lib/bridge.js";
import { resolveContext } from "./lib/context.js";
import { keepTenantFacts } from "./lib/tenantFacts.js";
import { QuoteApp } from "./ui/quoteApp.js";
import { TicketApp } from "./ui/ticketApp.js";
import { MeApp } from "./ui/meApp.js";
import { MembershipsApp } from "./ui/membershipsApp.js";
import { GiftCardsApp } from "./ui/giftCardsApp.js";
import { giftCardsRouteFrom } from "./lib/giftCardsPage.js";
import { GiftCardBalanceApp } from "./ui/giftCardBalanceApp.js";
import { balanceRouteFrom } from "./lib/giftCardBalance.js";
import { meRouteFrom } from "./lib/me.js";
import { membershipsRouteFrom } from "./lib/membershipsPage.js";
import { quoteTokenFrom } from "./lib/quotePage.js";
import { ticketTokensFrom } from "./lib/ticketPage.js";
import "./ui/styles.css";
// B130: styles.css defines NO tokens (the B123-class fork is dead, walled in
// styles.test.ts) — @transact/ui tokens.css is the one source and load order
// is no longer load-bearing; host embed themes still win (inline overrides).
import "@transact/ui/src/fonts.css";
import "@transact/ui/src/tokens.css";

// B397 measured review #1 (2026-08-20): the exemplar's darkness is not
// tenant- or preference-themeable — the HOSTED face boots dark and light
// stays behind the toggle. B403 finding #23 (b) overrules the framed
// exception: 58:238's widget is drawn DARK riding the §9.2 bridge, so the
// EMBEDDED document boots the same dark sheet. The host page still owns
// its canvas (main.storefront.embedded stays transparent) and its theme
// hints still win — inline custom properties beat both attribute sets.
// B552 (c2) — before anything can throw: uncaught errors and unhandled
// rejections report to client.error.report (the api client wires the
// destination the moment it knows the API and the tenant; ?debug=1 mirrors).
observer.boot({ app: "storefront", appVersion: import.meta.env.TRANSACT_BUILD, target: window, location: window.location, storage: safeStorage(), userAgent: navigator.userAgent });
bootTransactTheme();
// B830 — a stranger tenant's remembered facts (name, theme, publishable key, bearer) go before any page reads them; B831 — a site-key boot is keyed by its site key.
const bootEnv = import.meta.env as Record<string, string | undefined>;
const bootContext = resolveContext({ href: window.location.href, framed: window.self !== window.top });
keepTenantFacts(safeSessionStorage(), bootContext.tenantId ?? bootEnv["VITE_TENANT_ID"], bootContext.siteKey);

const el = document.getElementById("root");
if (el === null) throw new Error("storefront root element missing");
// B549 (7) — the organizer's hosted quote page: `/q#<token>` renders the
// quote alone (no catalog boot, no guest mint — the signed token is the credential).
const quoteToken = quoteTokenFrom(window.location);
// B606 (d) — the buyer's ticket page: `/t#<token>[,<token>…]` from the ticket email (the token is the credential).
const ticketTokens = ticketTokensFrom(window.location);
// B632 (c) — the guest's own pages: `/me` (sign in with a link) and `/me#<link>` (the email's link, opened once).
const meRoute = meRouteFrom(window.location);
// B709 — the buyer's Memberships page: `/memberships?tenant=<id>` (632:2 · 634:77).
const membershipsRoute = membershipsRouteFrom(window.location);
// B1020 — the buyer's Gift cards page: `/gift-cards?tenant=<id>` (842:2 · 843:2).
const giftCardsRoute = giftCardsRouteFrom(window.location);
// B1022 d.2 — the public balance check: `/gift-cards/balance?tenant=<id>` (845:2), no sign-in.
const balanceRoute = balanceRouteFrom(window.location);
const apiBaseUrl = (import.meta.env as Record<string, string | undefined>)["VITE_API_BASE_URL"] ?? "";
// B963 — a refused submit hands the keyboard to the first refused field.
installRefusalFocus(document);
createRoot(el).render(
  <StrictMode>
    {/* B552 (c2-embed) — embedded, the host page hears the crash and its Ref (a hosted page has no pinned host: the relay stays quiet). */}
    <ErrorBoundary onError={(error, stack) => observer.crash(error, stack).then((ref) => { hostErrors.send("CRASH", ref); return ref; })}>
      {quoteToken !== undefined ? (
        <QuoteApp token={quoteToken} apiBaseUrl={apiBaseUrl} go={(url) => window.location.assign(url)} />
      ) : ticketTokens !== undefined ? (
        <TicketApp tokens={ticketTokens} apiBaseUrl={apiBaseUrl} />
      ) : meRoute !== undefined ? (
        <MeApp route={meRoute} apiBaseUrl={apiBaseUrl} clearHash={(path) => window.history.replaceState(null, "", path)} />
      ) : membershipsRoute !== undefined ? (
        <MembershipsApp route={membershipsRoute} apiBaseUrl={apiBaseUrl} />
      ) : balanceRoute !== undefined ? (
        <GiftCardBalanceApp route={balanceRoute} apiBaseUrl={apiBaseUrl} />
      ) : giftCardsRoute !== undefined ? (
        <GiftCardsApp route={giftCardsRoute} apiBaseUrl={apiBaseUrl} />
      ) : (
        <App />
      )}
    </ErrorBoundary>
  </StrictMode>,
);

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function safeStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
